// src/utils/ga.ts
import { hasInternalModesAccess } from '../constants/roles';
import { redactEventParams, redactUrlForAnalytics } from './analyticsPrivacy';
import { utmParamsForEvent } from './utm';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Internal founder-admin routes excluded from GA4 collection.
// Matches "/mnb" and "/mnb/..." but not "/mnbfoo".
export function isInternalPath(pathname: string): boolean {
  return pathname === '/mnb' || pathname.startsWith('/mnb/');
}

/**
 * Internal traffic (prawmi-c5t4). GA4's "Internal traffic" data filter drops
 * events whose `traffic_type` parameter is `internal`, so the founders and the
 * autonomous test account stop inflating sign-ups, trials and key events.
 *
 * Who counts: the two founder roles behind `hasInternalModesAccess` (1133
 * super-admin + 1136, the same pair the backend treats as internal) and the
 * QA test account. The test account has no role, so it is matched by e-mail.
 * This pattern MIRRORS `TEST_AGENT_EMAIL_PATTERN` in
 * Firebase/src/utils/test-agent.ts (the frontend cannot import from
 * Firebase/); ga.test.ts fails if the two drift apart.
 */
export const TEST_AGENT_EMAIL_PATTERN = /^obywateltb\+agent(-[a-z0-9-]+)?@gmail\.com$/i;

const INTERNAL_TRAFFIC_KEY = 'ga_internal_traffic';

/** Sticky per browser: once an internal account signs in here, every later
 *  visit - public pages and signed-out browsing included - is tagged from the
 *  very first event. */
let internalTraffic = ((): boolean => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem(INTERNAL_TRAFFIC_KEY) === '1';
  } catch {
    return false;
  }
})();

export function isInternalTrafficUser(
  email: string | null | undefined,
  userRole: number | null
): boolean {
  return hasInternalModesAccess(userRole) || TEST_AGENT_EMAIL_PATTERN.test((email ?? '').trim());
}

export function isInternalTraffic(): boolean {
  return internalTraffic;
}

/** Tag this browser's GA4 traffic as internal from now on (idempotent). */
export function markInternalTraffic(): void {
  if (!internalTraffic) {
    internalTraffic = true;
    try {
      localStorage.setItem(INTERNAL_TRAFFIC_KEY, '1');
    } catch {
      // Storage blocked: the tag still applies for the rest of this page load.
    }
  }
  // `set` makes gtag attach the parameter to every later event, including the
  // page_view that MainLayout sends straight through window.gtag.
  window.gtag?.('set', { traffic_type: 'internal' });
}

// Ładowanie GA z wyłączonym auto page_view
export function loadGA(measurementId: string) {
  if (!measurementId) return;
  if (isInternalPath(window.location.pathname)) return;

  // Check if GA script already exists
  const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
  if (existingScript) return;
  
  window.dataLayer = window.dataLayer || [];
  window.gtag = function(){ window.dataLayer.push(arguments as any); } as any;

  // Consent defaults must fire before gtag config (consent mode v2)
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'denied',
    security_storage: 'granted',
  });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);

  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false, ...gaPageContext() });
  if (internalTraffic) window.gtag('set', { traffic_type: 'internal' });
}

/**
 * page_location (and page_referrer, when it needs cleaning) for a GA4 hit,
 * with e-mails and tokens taken out of the URLs (prawmi-odwr). Left alone,
 * gtag reads both straight from `document.location` and `document.referrer`,
 * which on /dane carry the registration e-mail and token - and so does the
 * referrer of the page after it.
 *
 * page_referrer is overridden only when redaction changes it: otherwise gtag
 * keeps deriving it on its own, and session attribution stays as it was.
 */
export function gaPageContext(): { page_location: string; page_referrer?: string } {
  const page_location = redactUrlForAnalytics(window.location.href);
  const referrer = typeof document !== 'undefined' ? document.referrer || '' : '';
  const safeReferrer = redactUrlForAnalytics(referrer);
  return safeReferrer === referrer
    ? { page_location }
    : { page_location, page_referrer: safeReferrer };
}

/**
 * The one page_view sender for both emitters - MainLayout (react-router) and
 * app/components/GoogleAnalytics.tsx (App Router).
 *
 * `set` first: automatically collected events (user_engagement, scroll,
 * form_start, ...) never pass through this module and would otherwise take
 * page_location from the raw address bar.
 *
 * page_path must NOT carry the query string: GA4 builds pagePathPlusQueryString
 * by appending the query it parses out of page_location to page_path, so a
 * page_path with the query produced doubled rows
 * ("/rejestracja?kod=dpszulc?kod=dpszulc").
 */
export function trackPageView(pagePath: string): void {
  if (isInternalPath(pagePath)) return;
  const context = gaPageContext();
  window.gtag?.('set', context);
  window.gtag?.('event', 'page_view', {
    ...context,
    page_path: pagePath,
    page_title: document.title,
  });
}

export function updateConsent(granted: boolean) {
  window.gtag?.('consent','update',{
    ad_storage: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
    functionality_storage: granted ? 'granted' : 'denied',
    security_storage: 'granted'
  });
}

// GA4 user-scoped properties (segment reporting: user_declared_role, user_segment).
// Same internal-path exclusion as track() so /mnb sessions stay out of GA.
export function setUserProperties(props: Record<string, any>) {
  if (isInternalPath(window.location.pathname)) return;
  window.gtag?.('set', 'user_properties', props);
}

/**
 * GA4 treats these event-parameter names as traffic-attribution input: sending
 * one on any event rewrites that session's acquisition source, which drops the
 * session into the "Unassigned" channel. Measured 2026-08-24: `watch_created`
 * carried {source:'auto'|'manual'} and moved 258 of 291 watch events plus 52 of
 * 90 key events out of their real channels, making organic conversion read as
 * near-zero. Event-specific values must therefore be namespaced by the caller;
 * this guard is the net that keeps a future call site from reintroducing it.
 *
 * The `utm_payload` stash is exempt - those ARE the campaign values, and they
 * are applied deliberately below (scoped by utmParamsForEvent).
 */
const RESERVED_ATTRIBUTION_PARAMS = [
  'source',
  'medium',
  'campaign',
  'term',
  'content',
  'campaign_id',
  'source_platform',
] as const;

/** Prefixes any reserved attribution key with the event name, e.g. `watch_created_source`. */
function namespaceReservedParams(
  eventName: string,
  params: Record<string, any>,
): Record<string, any> {
  const safe: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if ((RESERVED_ATTRIBUTION_PARAMS as readonly string[]).includes(key)) {
      safe[`${eventName}_${key}`] = value;
    } else {
      safe[key] = value;
    }
  }
  return safe;
}

export function track(eventName: string, params: Record<string, any> = {}) {
  if (isInternalPath(window.location.pathname)) return;
  const safeParams = {
    ...namespaceReservedParams(eventName, params),
    // Explicit as well as via gtag('set'), so the tag survives a gtag that
    // was (re)loaded after markInternalTraffic ran.
    ...(internalTraffic ? { traffic_type: 'internal' } : {}),
    // Last, so no caller can put a raw URL back (prawmi-odwr).
    ...gaPageContext(),
  };
  // Only in the session that saw the campaign URL, or on sign_up (utm.ts).
  const utm = utmParamsForEvent(eventName);
  window.gtag?.('event', eventName, redactEventParams({ ...utm, ...safeParams }));
}

/** EDU series (A-2026-08-24-7). Never pass a bare `source` param - see RESERVED_ATTRIBUTION_PARAMS. */
export const RESEARCH_MODE_SELECTED_EVENT = 'research_mode_selected';
export const EDU_PLAN_VIEWED_EVENT = 'edu_plan_viewed';

export type CheckoutBillingPeriod = 'monthly' | 'semester';

/** Chip / preference change on /czat. `mode` is the selected research mode key. */
export function trackResearchModeSelected(mode: string): void {
  track(RESEARCH_MODE_SELECTED_EVENT, { mode });
}

/**
 * Impression of the Edukacja plan card. Use `surface` (not `source`) for the
 * page that showed the card - `source` is a reserved attribution key.
 */
export function trackEduPlanViewed(params: { surface: 'cennik' | 'subskrypcja' }): void {
  track(EDU_PLAN_VIEWED_EVENT, { surface: params.surface });
}

/**
 * Which Stripe Checkout Session creator a begin_checkout belongs to. Mirrors
 * `CheckoutProduct` in Firebase `utils/ga-measurement-protocol.ts`, which
 * sends the server twin of this event for visitors without analytics consent.
 */
export type CheckoutProduct = 'subscription' | 'analiza' | 'credits' | 'team';

/**
 * Checkout start. Sends both `planKey` (EDU-11 contract) and legacy `plan`
 * so existing begin_checkout reports keep working. `billing_period` is the
 * settlement variant (monthly | semester), sent whenever the caller passes it
 * (every subscription checkout does). `product` tells the checkout paths apart
 * and defaults to `subscription`, the only path that existed before it;
 * `planKey` then carries the plan, pack, analysis variant or team package.
 * Never use `source` for any of this.
 */
export function trackBeginCheckout(params: {
  planKey: string;
  billingPeriod?: CheckoutBillingPeriod;
  product?: CheckoutProduct;
  currency?: string;
  value?: number;
}): void {
  track('begin_checkout', {
    planKey: params.planKey,
    plan: params.planKey,
    product: params.product ?? 'subscription',
    ...(params.billingPeriod ? { billing_period: params.billingPeriod } : {}),
    ...(params.currency ? { currency: params.currency } : {}),
    ...(typeof params.value === 'number' ? { value: params.value } : {}),
  });
}

/**
 * GA4 client_id from the `_ga` cookie (`GA1.1.XXXXXXXXXX.YYYYYYYYYY`).
 * Used as Stripe checkout metadata so the webhook can stitch a server-side
 * `purchase` onto the same browser session. Null when consent never granted
 * (no cookie) or the cookie is malformed.
 */
export function getGaClientId(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.([^;]+)/);
  if (!match) return null;
  const id = match[1].trim();
  return /^\d{1,20}\.\d{1,20}$/.test(id) ? id : null;
}

